import { useEffect, useMemo, useRef, useState } from "react";
import { setAnalyticsSuppressed } from "@/lib/analytics";
import { sendEvent } from "@/lib/firstPartyAnalytics";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import RichText from "@/components/RichText";
import VideoPlayer from "@/components/VideoPlayer";
import AthleteSocialLinks from "@/components/AthleteSocialLinks";
import ProfileModuleShell from "@/components/athlete/ProfileModuleShell";
import ModuleDiagnostics from "@/components/athlete/ModuleDiagnostics";
import CampaignReturnLink from "@/components/CampaignReturnLink";
import { iconFor, type QAItem, type BookingCTA } from "@/components/athlete/ProfileFieldEditors";
import { publicUrl } from "@/lib/storage";
import { athleteImageUrl, athletePlaceholder } from "@/lib/athleteImage";
import { buildShareContent } from "@/lib/share";
import { applyHeadMeta } from "@/lib/headMeta";

import SupportSection, { type PublicSupporter } from "@/components/support/SupportSection";
import AthleteSupportHub from "@/components/support/AthleteSupportHub";
import WorkWithAthleteSection from "@/components/support/WorkWithAthleteSection";
import { useAuth } from "@/hooks/useAuth";
import {
  applyPreviewSimulation,
  buildProfileModules,
  bookingCtaList,
  parseSimulation,
  primaryCtaHref as primaryCtaHrefFor,
  quickFactList,
  heroPosition,
  validStats,
  plainText,
  richText,
  safeHttpUrl,
  shouldRenderRail,
  socialLinkList,
  text as cleanText,
  validCauses,
  validPhotos,
  validProducts,
  validQA,
  validVideos,
  type ProfileAthlete,
  type ProfileRelations,
} from "@/lib/profileModules";
import { ArrowRight, Hash } from "lucide-react";
import { useHubLayoutFor } from "@/lib/featureFlags";
import { unwrapQuotes } from "@/lib/quotes";

type AthleteRow = ProfileAthlete & {
  slug: string;
  country: string | null;
  tagline: string | null;
  image_path: string | null;
  stats: { label: string; value: string }[];
  published: boolean;
  jersey_number: string | null;
  position_short: string | null;
  position_long: string | null;
  team_logo_path: string | null;
  qa: QAItem[] | null;
  booking_ctas: BookingCTA[] | null;
};

type CauseRow = { id: string; name: string; blurb: string | null; raised: number; goal: number; donate_url: string | null };
type ProductRow = {
  id: string;
  brand_name: string;
  product_name: string;
  category: string | null;
  price: string | null;
  image_path: string | null;
  image_paths: string[];
  product_url: string | null;
  quote: string | null;
  is_house_brand: boolean;
};
type RelatedAthlete = {
  id: string; slug: string; name: string; sport: string; image_path: string | null;
  position_short: string | null;
};

const AthleteProfile = () => {
  const { slug } = useParams<{ slug: string }>();
  const { isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  // Admin-only preview mode. A non-admin with the same URL gets the ordinary
  // public profile: the flag is ignored unless the verified admin role is set.
  const previewMode = searchParams.get("adminPreview") === "1" && isAdmin;
  const hubLayout = useHubLayoutFor({ previewMode, layoutParam: searchParams.get("layout") });
  const simulation = useMemo(
    () => (previewMode ? parseSimulation(searchParams.get("sim")) : {}),
    [previewMode, searchParams],
  );
  const simulating = Object.keys(simulation).length > 0;
  // Admin-only, URL-scoped commerce override: never written, never public.
  const commerceOverride = previewMode && searchParams.get("commercePreview") === "1";
  useEffect(() => {
    setAnalyticsSuppressed(previewMode);
    return () => setAnalyticsSuppressed(false);
  }, [previewMode]);
  const [athleteRow, setAthleteRow] = useState<AthleteRow | null>(null);
  const [causes, setCauses] = useState<CauseRow[]>([]);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [teams, setTeams] = useState<{ id: string; slug: string; name: string; logo_path: string | null }[]>([]);
  const [related, setRelated] = useState<RelatedAthlete[]>([]);
  const [supporters, setSupporters] = useState<PublicSupporter[]>([]);
  const [supportersLoaded, setSupportersLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [status, setStatus] = useState<{ message: string; updated_at: string } | null>(null);
  const [heroImgFailed, setHeroImgFailed] = useState(false);
  const [hasBackOption, setHasBackOption] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    // Bounded load: never leave the page on a skeleton forever.
    const timeoutId = window.setTimeout(() => {
      if (!cancelled) {
        setLoadError(true);
        setLoading(false);
      }
    }, 15000);
    (async () => {
      setLoading(true);
      setLoadError(false);
      setNotFound(false);
      setHeroImgFailed(false);
      setSupportersLoaded(false);
      try {
      const baseQuery = supabase.from("athletes").select("*").eq("slug", slug);
      const { data: a, error: aErr } = await (previewMode ? baseQuery : baseQuery.eq("published", true)).maybeSingle();
      if (aErr) throw aErr;
      if (!a) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setAthleteRow(a as any);


      const [caRes, paRes, taRes] = await Promise.all([
        supabase.from("cause_athletes").select("cause_id").eq("athlete_id", (a as any).id),
        supabase.from("product_athletes").select("product_id").eq("athlete_id", (a as any).id),
        supabase.from("athlete_teams").select("team_id").eq("athlete_id", (a as any).id),
      ]);
      const causeIds = (caRes.data ?? []).map((r: any) => r.cause_id);
      const productIds = (paRes.data ?? []).map((r: any) => r.product_id);
      const teamIds = (taRes.data ?? []).map((r: any) => r.team_id);

      const [cRes, pRes, tRes] = await Promise.all([
        causeIds.length
          ? supabase.from("causes").select("*").in("id", causeIds).eq("published", true)
          : Promise.resolve({ data: [] as any }),
        productIds.length
          ? supabase.from("brand_products").select("*").in("id", productIds).eq("published", true)
          : Promise.resolve({ data: [] as any }),
        teamIds.length
          ? supabase.from("teams").select("id,slug,name,logo_path").in("id", teamIds).eq("published", true)
          : Promise.resolve({ data: [] as any }),
      ]);
      setCauses(((cRes as any).data ?? []) as any);
      setProducts(((pRes as any).data ?? []) as any);
      const teamsData = ((tRes as any).data ?? []) as { id: string; slug: string; name: string; logo_path: string | null }[];
      setTeams(teamsData);

      // Related athletes: same team → fallback same sport
      let relatedRows: RelatedAthlete[] = [];
      if (teamsData.length > 0) {
        const { data: teamMemberships } = await supabase
          .from("athlete_teams")
          .select("athlete_id")
          .in("team_id", teamsData.map((t) => t.id))
          .neq("athlete_id", (a as any).id);
        const otherIds = Array.from(new Set((teamMemberships ?? []).map((r: any) => r.athlete_id)));
        if (otherIds.length) {
          const { data } = await supabase
            .from("athletes")
            .select("id,slug,name,sport,image_path,position_short")
            .in("id", otherIds)
            .eq("published", true)
            .limit(6);
          relatedRows = ((data ?? []) as any) as RelatedAthlete[];
        }
      }
      if (relatedRows.length < 3) {
        const { data } = await supabase
          .from("athletes")
          .select("id,slug,name,sport,image_path,position_short")
          .eq("sport", (a as any).sport)
          .eq("published", true)
          .neq("id", (a as any).id)
          .limit(6);
        const bySport = ((data ?? []) as any) as RelatedAthlete[];
        const seen = new Set(relatedRows.map((r) => r.id));
        bySport.forEach((r) => { if (!seen.has(r.id)) { relatedRows.push(r); seen.add(r.id); } });
      }
      setRelated(relatedRows.slice(0, 3));
      setLoading(false);
      } catch (err) {
        console.error("Athlete profile load failed:", err);
        if (!cancelled) {
          setLoadError(true);
          setLoading(false);
        }
      } finally {
        window.clearTimeout(timeoutId);
      }
    })();
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [slug, previewMode, reloadKey]);

  // Public supporter wall — only the rows the viewer is allowed to read.
  useEffect(() => {
    if (!athleteRow?.id) return;
    let active = true;
    setSupportersLoaded(false);
    supabase
      .from("public_supporters")
      .select("id,supporter_name,message,transaction_timestamp")
      .eq("athlete_id", athleteRow.id)
      .order("transaction_timestamp", { ascending: false })
      .limit(12)
      .then(({ data }) => {
        if (!active) return;
        setSupporters((data ?? []) as PublicSupporter[]);
        setSupportersLoaded(true);
      });
    return () => { active = false; };
  }, [athleteRow?.id]);

  // Live status (realtime)
  useEffect(() => {
    if (!athleteRow?.id) return;
    let active = true;
    supabase
      .from("athlete_status")
      .select("message, updated_at")
      .eq("athlete_id", athleteRow.id)
      .maybeSingle()
      .then(({ data }) => { if (active) setStatus((data as any) ?? null); });
    const channel = supabase
      .channel(`status-${athlete.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "athlete_status", filter: `athlete_id=eq.${athlete.id}` },
        (payload) => {
          if (payload.eventType === "DELETE") setStatus(null);
          else setStatus(payload.new as any);
        }
      )
      .subscribe();
    return () => { active = false; supabase.removeChannel(channel); };
  }, [athleteRow?.id]);

  const baseRelations: ProfileRelations = useMemo(
    () => ({ causes, products, related, publicSupporters: supporters, supportersLoaded }),
    [causes, products, related, supporters, supportersLoaded],
  );

  // Simulation is a local overlay only — nothing here is written back to the database.
  const simulated = useMemo(
    () => (athleteRow && simulating ? applyPreviewSimulation(athleteRow, baseRelations, simulation) : null),
    [athleteRow, baseRelations, simulating, simulation],
  );
  const athlete = simulated?.athlete ?? athleteRow;
  const relations = simulated?.relations ?? baseRelations;

  const resolvePath = (p: string) => (/^(https?:|\/)/.test(p) ? p : publicUrl(p));

  const modules = useMemo(
    () => (athlete ? buildProfileModules(athlete, relations, resolvePath) : null),
    [athlete, relations],
  );

  // The preview host reads readiness straight from the live page, so admin
  // diagnostics can never drift from what the public profile actually renders.
  useEffect(() => {
    if (!previewMode || !modules) return;
    (window as unknown as { __siisPreviewModules?: unknown }).__siisPreviewModules = {
      slug,
      published: Boolean(athleteRow?.published),
      modules,
    };
    if (window.parent === window) return;
    window.parent.postMessage(
      { type: "siis:preview-modules", slug, published: Boolean(athleteRow?.published), modules },
      window.location.origin,
    );
  }, [previewMode, modules, slug, athleteRow?.published]);

  // Social-preview metadata for this profile. Plain text only — the tagline and
  // a sanitized biography excerpt, never raw biography markup.
  useEffect(() => {
    if (!athleteRow) return;
    const content = buildShareContent({
      name: athleteRow.name,
      slug: athleteRow.slug,
      tagline: athleteRow.tagline,
      bio: athleteRow.bio,
      imageUrl: athleteRow.image_path ? athleteImageUrl(athleteRow.image_path) : undefined,
    });
    return applyHeadMeta({
      title: content.title,
      description: content.description,
      url: content.url,
      image: content.imageUrl,
      imageAlt: content.imageAlt,
    });
  }, [athleteRow]);



  // First-party profile view: once per athlete per page load, never in Admin Preview.
  const viewSentFor = useRef<string | null>(null);
  useEffect(() => {
    // Any Admin Preview URL is excluded outright (even before the admin role finishes loading).
    if (searchParams.get("adminPreview") === "1") return;
    if (!athleteRow?.id || !athleteRow.published || previewMode || viewSentFor.current === athleteRow.id) return;
    viewSentFor.current = athleteRow.id;
    sendEvent("athlete_profile_view", { athleteId: athleteRow.id, metadata: { layout: hubLayout ? "hub" : "classic" } });
  }, [athleteRow?.id, athleteRow?.published, previewMode, hubLayout, searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container py-12 space-y-6" aria-busy="true">
          <div className="h-[340px] rounded-[2rem] bg-muted animate-pulse" />
          <div className="h-40 rounded-2xl bg-muted animate-pulse" />
          <span className="sr-only">Loading athlete profile…</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container py-24 text-center space-y-4">
          <h1 className="font-display text-4xl">This profile didn’t load</h1>
          <p className="text-muted-foreground">
            Something went wrong while loading this profile. Please try again.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => setReloadKey((k) => k + 1)}
              className="min-h-11 rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground"
            >
              Retry
            </button>
            <Link to="/athletes" className="min-h-11 inline-flex items-center rounded-full bg-muted px-6 text-sm font-semibold">
              Back to roster
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !athlete || !modules) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container py-24 text-center">
          <h1 className="font-display text-4xl mb-4">Athlete not found</h1>
          <Link to="/athletes" className="text-primary underline">Back to roster</Link>
        </div>
      </div>
    );
  }

  const firstName = athlete.name.split(" ")[0];
  const heroImgSrc = athlete.image_path ? athleteImageUrl(athlete.image_path) : null;
  const showBrandedPanel = !heroImgSrc || heroImgFailed;
  const teamLogo = publicUrl(athlete.team_logo_path) ?? publicUrl(teams[0]?.logo_path ?? null);

  const photos = validPhotos(athlete, resolvePath);
  const videos = validVideos(athlete);
  const qa = validQA(athlete);
  const facts = quickFactList(athlete);
  const ctas = bookingCtaList(athlete);
  const ctaHref = primaryCtaHrefFor(athlete);
  const links = socialLinkList(athlete);
  const readyCauses = validCauses(relations);
  const readyProducts = validProducts(relations);
  const bio = richText(athlete.bio);
  const bioIsHtml = !!bio && /<\/?[a-z][\s\S]*>/i.test(bio);
  const heroQuote = cleanText(athlete.tagline);
  const stats = validStats(athlete);
  const heroPos = heroPosition(athlete);

  const railOn = shouldRenderRail(modules, {
    facts: facts.length,
    links: (links.custom_links?.length ?? 0) + [links.instagram_url, links.tiktok_url, links.facebook_url, links.youtube_url, links.linkedin_url].filter(Boolean).length,
    ctas: ctas.length,
    related: related.length,
    mantraChars: (cleanText(athlete.mantra_quote) ?? "").length,
  });
  const aboutAndQa = modules.about.active && modules.qa.active;
  const initials = athlete.name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");


  // The hub sits straight after the hero when support is live or the profile is
  // sparse; otherwise a short About introduction comes first.
  const hubAfterHero = modules.directSupport.active || !modules.about.active;
  const supportHubNode = (
    <AthleteSupportHub
      athleteId={athlete.id}
      athleteName={athlete.name}
      firstName={firstName}
      slug={athlete.slug}
      tagline={athlete.tagline}
      bio={athlete.bio}
      imageUrl={heroImgSrc}
      directSupport={modules.directSupport.active}
      cause={modules.causes.active}
      partnership={modules.partnershipInquiry.active}
      commerceOverride={commerceOverride}
      adminPreview={previewMode}
      athletePublished={Boolean(athleteRow?.published)}
    />

  );


  /* ------------------------------------------------ reusable module nodes */

  // Full-width editorial pull-quote. Hidden when empty, and never a duplicate
  // of the hero quote (readiness is decided in profileModules).
  const mantraNode = (
    <ProfileModuleShell active={modules.mantra.active} title="Her Mantra" tone="accent" width="full" key="mantra">
      <blockquote className="serif text-2xl md:text-4xl leading-tight text-foreground not-italic font-semibold break-words hyphens-none max-w-4xl">
        <span className="text-primary text-3xl leading-none">“</span>
        {hubLayout ? unwrapQuotes(cleanText(athlete.mantra_quote)) : cleanText(athlete.mantra_quote)}
        <span className="text-primary text-3xl leading-none">”</span>
      </blockquote>
    </ProfileModuleShell>
  );

  // Hero quote — the admin Tagline, plain text only.
  const heroQuoteNode = (
    <ProfileModuleShell active={modules.heroQuote.active} title="Hero quote" titleVisible={false} tone="bare" width="full" key="hero-quote">
      <p className="serif text-lg md:text-2xl leading-snug text-foreground break-words hyphens-none max-w-3xl">
        <span className="text-primary">“</span>
        {hubLayout ? unwrapQuotes(heroQuote) : heroQuote}
        <span className="text-primary">”</span>
      </p>
    </ProfileModuleShell>
  );

  const statsNode = (
    <ProfileModuleShell active={modules.stats.active} title="By the Numbers" width="full" key="stats">
      <dl className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,9rem),1fr))]">
        {stats.map((s, i) => (
          <div key={`${s.label}-${i}`} className="rounded-xl bg-muted/50 p-3 min-w-0">
            <dt className="text-xs uppercase tracking-wider text-muted-foreground break-words hyphens-none">{s.label}</dt>
            <dd className="font-display text-2xl leading-none mt-1 break-words hyphens-none">{s.value}</dd>
          </div>
        ))}
      </dl>
    </ProfileModuleShell>
  );

  const quickFactsNode = (
    <ProfileModuleShell active={modules.quickFacts.active} title="Quick Facts" width={railOn ? "rail" : "full"} key="facts">
      <dl
        className={`divide-y divide-border ${
          railOn
            ? ""
            : "sm:grid sm:gap-x-8 sm:divide-y-0 sm:[grid-template-columns:repeat(auto-fit,minmax(min(100%,15rem),1fr))]"
        }`}
      >
        {facts.map((f) => (
          <div
            key={f.label}
            className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 py-2 text-sm border-border sm:border-b min-w-0"
          >
            <dt className="text-muted-foreground min-w-0 break-words hyphens-none">{f.label}</dt>
            <dd className="font-semibold min-w-0 max-w-full break-words hyphens-none text-left sm:text-right">
              {f.value}
            </dd>
          </div>
        ))}
      </dl>
    </ProfileModuleShell>
  );

  const connectNode = (
    <ProfileModuleShell active={modules.socialLinks.active} title="Connect" width={railOn ? "rail" : "full"} key="connect">
      <AthleteSocialLinks
        links={links}
        variant="full"
        className={railOn ? "flex-col !gap-2 [&>a]:w-full [&>a]:justify-start [&>a]:min-h-[44px]" : "[&>a]:min-h-[44px]"}
      />
    </ProfileModuleShell>
  );

  const ctaNode = (
    <ProfileModuleShell
      active={modules.bookingCta.active}
      title={`Work With ${firstName}`}
      titleVisible={false}
      tone="accent"
      width={railOn ? "rail" : "full"}
      key="cta"
    >
      <p className="font-display text-xl uppercase leading-tight break-words hyphens-none">
        Work With {firstName}
      </p>
      {ctas.length > 0 && (
        <ul className="mt-4 space-y-1">
          {ctas.slice(0, 6).map((c, i) => (
            <li key={i}>
              <a
                href={c.url}
                target={c.url.startsWith("http") ? "_blank" : undefined}
                rel="noopener noreferrer"
                className="flex min-h-[44px] items-center gap-2 py-2 text-sm font-semibold hover:text-primary focus-visible:text-primary transition-colors break-words"
              >
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-background/70 text-primary shrink-0">
                  <ArrowRight className="w-3.5 h-3.5" />
                </span>
                {c.label}
              </a>
            </li>
          ))}
        </ul>
      )}
      {ctaHref && (
        <a
          href={ctaHref}
          target={ctaHref.startsWith("http") ? "_blank" : undefined}
          rel="noopener noreferrer"
          className="mt-4 inline-flex min-h-[44px] items-center justify-center gap-2 w-full px-4 py-2.5 rounded-full bg-secondary text-secondary-foreground font-semibold text-sm hover:bg-primary hover:text-primary-foreground transition-colors"
        >
          Inquire Now <ArrowRight className="w-4 h-4" />
        </a>
      )}
    </ProfileModuleShell>
  );

  const relatedNode = (
    <ProfileModuleShell active={modules.relatedAthletes.active} title="Related Athletes" width={railOn ? "rail" : "full"} key="related">
      <p className="text-xs text-muted-foreground -mt-2 mb-3">See more athletes like {firstName}</p>
      <ul className={`space-y-3 ${railOn ? "" : "sm:grid sm:gap-3 sm:space-y-0 sm:[grid-template-columns:repeat(auto-fit,minmax(min(100%,14rem),1fr))]"}`}>
        {related.map((r) => (
          <li key={r.id}>
            <Link
              to={`/athlete/${r.slug}`}
              className="flex min-h-[44px] items-center gap-3 hover:bg-muted/60 rounded-xl p-1.5 -m-1.5 transition-colors"
            >
              <div className="w-11 h-11 rounded-xl bg-muted overflow-hidden shrink-0">
                <img
                  src={athleteImageUrl(r.image_path)}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).src = athletePlaceholder; }}
                />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-sm truncate">{r.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {r.position_short ? `${r.position_short} · ` : ""}{r.sport}
                </p>
              </div>
            </Link>
          </li>
        ))}
      </ul>
      <Link to="/athletes" className="mt-4 inline-flex min-h-[44px] items-center gap-1 text-sm text-primary font-semibold">
        View All Athletes <ArrowRight className="w-3 h-3" />
      </Link>
    </ProfileModuleShell>
  );

  // Facts + Connect + CTA sit directly under the hero on mobile, and in the rail on desktop.
  const clusterNodes = [quickFactsNode, connectNode, ctaNode];
  const clusterActive =
    modules.quickFacts.active || modules.socialLinks.active || modules.bookingCta.active;

  const renderHero = (actions: import("react").ReactNode) => (
          <>
            {/* HERO — identity, always rendered */}
            <section className="relative overflow-hidden rounded-2xl bg-secondary text-secondary-foreground shadow-bold">
              <div className="grid md:grid-cols-[minmax(0,3fr)_minmax(260px,2fr)] items-stretch">
                <div className="p-6 md:p-8 lg:p-9 relative z-10 flex flex-col justify-center min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-sm font-semibold text-primary-on-navy tracking-wide">
                      {athlete.jersey_number && <span>#{athlete.jersey_number}</span>}
                      {athlete.jersey_number && heroPos && <span className="opacity-40 mx-2">|</span>}
                      {heroPos && <span>{heroPos}</span>}
                    </p>
                    {teamLogo && (
                      <div className="md:hidden bg-secondary-foreground/10 rounded-xl p-2 backdrop-blur">
                        <img src={teamLogo} alt="" className="h-8 w-auto object-contain" />
                      </div>
                    )}
                  </div>

                  <h1 className="font-display text-4xl sm:text-5xl md:text-6xl xl:text-7xl leading-[0.9] mt-2 text-balance uppercase break-words hyphens-none">
                    {athlete.name}
                  </h1>
                  {(teams[0] || athlete.sport || athlete.country) && (
                    <p className="mt-2 text-base md:text-lg opacity-90 break-words">
                      {teams[0] ? (
                        <Link to={`/team/${teams[0].slug}`} className="hover:text-primary-on-navy transition-colors font-semibold">
                          {teams[0].name}
                        </Link>
                      ) : null}
                      {teams[0] && (athlete.sport || athlete.country) && <br />}
                      <span className="opacity-80 break-words hyphens-none">
                        {[athlete.sport, athlete.country].filter(Boolean).join(" · ")}
                      </span>
                    </p>
                  )}

                  {/* Primary actions live in the support hub below — no duplicate
                      favourite or share controls here. */}


                  {actions}

                  {status && (
                    <div className="mt-5 relative inline-flex max-w-md">
                      <div className="bg-background text-foreground rounded-2xl px-4 py-2.5 shadow-bold">
                        <p className="text-[10px] uppercase tracking-wider text-primary font-bold flex items-center gap-1.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                          Live status
                        </p>
                        <RichText html={status.message} className="text-sm mt-0.5" />
                      </div>
                    </div>
                  )}
                </div>

                <div
                  className={`relative ${
                    showBrandedPanel
                      ? "h-[180px] sm:h-[220px] md:h-auto md:min-h-[300px] md:max-h-[440px]"
                      : "h-[280px] sm:h-[340px] md:h-auto md:min-h-[320px] md:max-h-[440px]"
                  }`}
                >
                  {showBrandedPanel ? (
                    <div className="h-full w-full flex md:flex-col items-center justify-center gap-4 px-6 py-5 bg-gradient-hero border-t md:border-t-0 md:border-l border-secondary-foreground/10">
                      <span className="font-display text-3xl md:text-5xl leading-none text-primary-foreground/95 tracking-tight">
                        {initials}
                      </span>
                      <span className="text-[10px] md:text-xs uppercase tracking-[0.25em] text-primary-foreground/70 text-center break-words">
                        Say It In Sports
                      </span>
                    </div>
                  ) : (
                    <img
                      src={heroImgSrc ?? athletePlaceholder}
                      alt={athlete.name}
                      className="absolute inset-0 w-full h-full object-cover object-top"
                      onError={() => setHeroImgFailed(true)}
                    />
                  )}
                  {teamLogo && (
                    <div className="hidden md:flex absolute top-4 right-4 bg-secondary-foreground/10 rounded-xl p-2 backdrop-blur">
                      <img src={teamLogo} alt="" className="h-10 w-auto object-contain" />
                    </div>
                  )}
                </div>

              </div>
            </section>
          </>
  );

  /* ---------------------------------------------- Business Hub (flagged) */
  if (hubLayout) {
    const jump = (id: string) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.setAttribute("tabindex", "-1");
      (el as HTMLElement).focus({ preventScroll: true });
    };
    const knowActive =
      modules.heroQuote.active || modules.about.active || modules.qa.active || modules.quickFacts.active ||
      modules.stats.active || modules.mantra.active || modules.causes.active || modules.photos.active ||
      modules.videos.active || modules.socialLinks.active;
    // One inquiry experience: the inquiry form when it exists, otherwise the booking links.
    const workInquiry = modules.partnershipInquiry.active;
    const workLinks = !workInquiry && modules.bookingCta.active;
    const workActive = workInquiry || workLinks || modules.brandRelationships.active;
    const backHubNode = (
      <AthleteSupportHub
        mode="back"
        onAvailability={setHasBackOption}
        athleteId={athlete.id}
        athleteName={athlete.name}
        firstName={firstName}
        slug={athlete.slug}
        tagline={athlete.tagline}
        bio={athlete.bio}
        imageUrl={heroImgSrc}
        directSupport={modules.directSupport.active}
        commerceOverride={commerceOverride}
        adminPreview={previewMode}
        athletePublished={Boolean(athleteRow?.published)}
      />
    );
    const heroAction =
      "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-secondary";
    const heroActions = (
      <div className="mt-5 flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => jump("follow-her")} className={`${heroAction} bg-primary-strong text-primary-foreground hover:bg-background hover:text-foreground`}>
            Follow {firstName}
          </button>
          {hasBackOption && <button type="button" onClick={() => jump("back-her")} className={`${heroAction} border border-secondary-foreground/30 hover:bg-secondary-foreground/10`}>
            Back Her Game
          </button>}
        </div>
        {(workInquiry || workLinks) && (
          <button type="button" onClick={() => jump("work-with-her")} className="self-start inline-flex min-h-[44px] items-center gap-1 text-xs font-semibold opacity-80 hover:opacity-100 hover:text-primary-on-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded">
            Brands & media: work with {firstName} <ArrowRight className="w-3 h-3" aria-hidden />
          </button>
        )}
      </div>
    );
    const SectionLabel = ({ id, children }: { id: string; children: string }) => (
      <h2 id={id} className="flex items-center gap-3 pt-4 text-xs font-bold uppercase tracking-[0.25em] text-primary-strong">
        {children}<span aria-hidden className="h-px flex-1 bg-border" />
      </h2>
    );

    return (
      <div className="min-h-screen bg-background">
        {previewMode && (
          <div role="region" aria-label="Admin preview notice" className="bg-primary-strong px-4 py-2 text-center text-xs font-semibold text-primary-foreground">
            Admin preview · Business Hub layout (not public){!athleteRow?.published && " · unpublished draft"}
            {simulating && " · Simulation — not saved"}
          </div>
        )}
        <Navbar />
        <nav aria-label="Breadcrumb" className="sticky top-16 z-40 bg-background/90 backdrop-blur border-b border-border/50">
          <div className="container py-2.5 flex min-w-0 flex-col items-start gap-1 text-sm">
            <CampaignReturnLink />
            <Link to="/athletes" className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full bg-muted px-4 py-1.5 font-semibold transition-colors hover:bg-secondary hover:text-secondary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
              <span aria-hidden>←</span> All athletes
            </Link>
          </div>
        </nav>
        <main className="container py-4 md:py-6 lg:py-8" style={{ paddingLeft: "max(1rem, env(safe-area-inset-left))", paddingRight: "max(1rem, env(safe-area-inset-right))", paddingBottom: "calc(2rem + env(safe-area-inset-bottom))" }}>
          <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 md:gap-6">
            {renderHero(heroActions)}

            {knowActive && (
              <section aria-labelledby="know-her-label" className="flex flex-col gap-5 md:gap-6 scroll-mt-32">
                <SectionLabel id="know-her-label">Know Her</SectionLabel>
                {heroQuoteNode}
                {(modules.about.active || modules.qa.active) && (
                  <div className={aboutAndQa ? "grid md:grid-cols-2 gap-5" : "flex"}>
                    <ProfileModuleShell active={modules.about.active} title={`About ${firstName}`} width={aboutAndQa ? "half" : "full"}>
                      <div className="text-sm md:text-[15px] leading-relaxed text-foreground/90 break-words hyphens-none">
                        {bioIsHtml ? <RichText html={bio!} /> : <p>{bio}</p>}
                      </div>
                    </ProfileModuleShell>
                    <ProfileModuleShell active={modules.qa.active} title="Get to Know Her" width={aboutAndQa ? "half" : "full"}>
                      <ul className="space-y-3">
                        {qa.map((q, i) => {
                          const Icon = iconFor(q.icon);
                          return (
                            <li key={i} className="flex gap-3 min-w-0">
                              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary/15 text-primary shrink-0"><Icon className="w-4 h-4" /></span>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold break-words">{q.question}</p>
                                <p className="text-sm text-muted-foreground break-words">{q.answer}</p>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </ProfileModuleShell>
                  </div>
                )}
                {(modules.quickFacts.active || modules.socialLinks.active) && (
                  <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(100%,17rem),1fr))] items-start">
                    {quickFactsNode}
                    {connectNode}
                  </div>
                )}
                {statsNode}
                {mantraNode}
                <PhotosSection photos={modules.photos.active ? photos : []} />
                <VideosSection videos={modules.videos.active ? videos : []} />
                <ProfileModuleShell id="causes" active={modules.causes.active} title="What Matters to Her">
                  <div className="grid gap-3 sm:grid-cols-2">
                    {readyCauses.slice(0, 4).map((c) => (
                      <Link key={c.id} to={`/cause/${c.id}`} className="flex items-start gap-3 rounded-xl bg-muted/40 p-3 hover:bg-muted/60 transition-colors min-w-0">
                        <span className="w-10 h-10 rounded-full bg-primary/15 text-primary inline-flex items-center justify-center shrink-0"><Hash className="w-4 h-4" /></span>
                        <span className="min-w-0">
                          <span className="block font-semibold text-sm break-words">{c.name}</span>
                          {plainText(c.blurb) && <span className="block text-xs text-muted-foreground mt-1 line-clamp-2 break-words">{plainText(c.blurb)}</span>}
                        </span>
                      </Link>
                    ))}
                  </div>
                </ProfileModuleShell>
              </section>
            )}

            {/* FOLLOW HER — latest update + follow */}
            <div className="flex flex-col gap-4">
              <AthleteSupportHub
                mode="follow"
                athleteId={athlete.id}
                athleteName={athlete.name}
                firstName={firstName}
                slug={athlete.slug}
                tagline={athlete.tagline}
                bio={athlete.bio}
                imageUrl={heroImgSrc}
                adminPreview={previewMode}
                athletePublished={Boolean(athleteRow?.published)}
              />
            </div>

            {/* BACK HER — Direct Support and/or Her Collection only */}
            {backHubNode}
            {modules.directSupport.active && (
              <SupportSection athlete={athlete as never} supporters={supporters} showSupporterWall={modules.publicSupporters.active} />
            )}

            {workActive && (
              <section id="work-with-her" aria-labelledby="work-her-label" className="flex flex-col gap-5 md:gap-6 scroll-mt-32">
                <SectionLabel id="work-her-label">Work With Her</SectionLabel>
                <ProfileModuleShell active={modules.brandRelationships.active} title={`Brands supporting ${firstName}`}>
                  <div className="flex flex-wrap gap-3 items-center">
                    {readyProducts.slice(0, 10).map((p) => (
                      <Link key={p.id} to={`/product/${p.id}`} className="h-20 w-40 shrink-0 rounded-xl bg-muted/50 hover:bg-muted flex items-center justify-center p-3 transition-colors">
                        {safeHttpUrl(publicUrl(p.image_path ?? "")) ? (
                          <img src={publicUrl(p.image_path!)!} alt={p.brand_name} className="max-h-full max-w-full object-contain" />
                        ) : (
                          <span className="text-xs font-bold uppercase tracking-wider text-center break-words">{p.brand_name}</span>
                        )}
                      </Link>
                    ))}
                  </div>
                </ProfileModuleShell>
                {workInquiry && (
                  <WorkWithAthleteSection athleteId={athlete.id} athleteName={athlete.name} partnershipInterests={cleanText(athlete.partnership_interests)} />
                )}
                {workLinks && ctaNode}
              </section>
            )}

            {relatedNode}

            {isAdmin && <ModuleDiagnostics modules={modules} />}
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-background">
      {previewMode && (
        <div className="bg-primary px-4 py-2 text-center text-xs font-semibold text-primary-foreground">
          Admin preview{!athleteRow?.published && " · unpublished draft"}
          {simulating && " · Simulation — not saved"}
        </div>
      )}
      <Navbar />
      <div className="sticky top-16 z-40 bg-background/90 backdrop-blur border-b border-border/50">
        <div className="container py-2.5 flex min-w-0 flex-col items-start gap-1 text-sm">
          <CampaignReturnLink />
          <div className="flex min-w-0 max-w-full items-center gap-2">
            <Link
              to="/athletes"
              className="inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-full bg-muted px-4 py-1.5 font-semibold transition-colors hover:bg-secondary hover:text-secondary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              <span aria-hidden>←</span> All athletes
            </Link>
            <span className="text-muted-foreground hidden min-w-0 truncate sm:inline">/ {athlete.name}</span>
          </div>
        </div>
      </div>

      <main
        className="container py-4 md:py-6 lg:py-8"
        style={{
          paddingLeft: "max(1rem, env(safe-area-inset-left))",
          paddingRight: "max(1rem, env(safe-area-inset-right))",
          paddingBottom: "calc(2rem + env(safe-area-inset-bottom))",
        }}
      >
        <div
          className={
            railOn
              ? "grid lg:grid-cols-[minmax(0,1fr)_320px] gap-5 lg:gap-6 items-start"
              : "flex flex-col gap-5 w-full max-w-5xl mx-auto"
          }
        >
          {/* MAIN COLUMN */}
          <div className="flex flex-col gap-5 md:gap-6 min-w-0">

            {renderHero(null)}

            {/* HERO QUOTE — the admin Tagline, plain text, hidden when empty */}
            {heroQuoteNode}

            {/* SUPPORT HUB — directly after the hero when support is active or the profile is sparse */}
            {hubAfterHero && supportHubNode}



            {/* IDENTITY CLUSTER — facts, connect, booking and mantra.
                In the rail on wide screens; otherwise a self-balancing grid in the flow. */}
            {clusterActive && (
              <div
                className={
                  railOn
                    ? "flex flex-col gap-6 lg:hidden"
                    : "grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(100%,17rem),24rem))] items-start"
                }
              >
                {clusterNodes}
              </div>
            )}


            {/* ABOUT + Q&A — balanced when both, full width when one */}
            {(modules.about.active || modules.qa.active) && (
              <div className={aboutAndQa ? "grid md:grid-cols-2 gap-5" : "flex"}>
                <ProfileModuleShell
                  active={modules.about.active}
                  title={`About ${firstName}`}
                  width={aboutAndQa ? "half" : "full"}
                  className={aboutAndQa ? "" : railOn ? "" : "max-w-4xl"}
                >
                  <div className="text-sm md:text-[15px] leading-relaxed text-foreground/90 break-words hyphens-none">
                    {bioIsHtml ? <RichText html={bio!} /> : <p>{bio}</p>}
                  </div>
                </ProfileModuleShell>

                <ProfileModuleShell
                  active={modules.qa.active}
                  title="Get to Know Her"
                  width={aboutAndQa ? "half" : "full"}
                  className={aboutAndQa ? "" : railOn ? "" : "max-w-4xl"}
                >
                  <ul className="space-y-3">
                    {qa.map((q, i) => {
                      const Icon = iconFor(q.icon);
                      return (
                        <li key={i} className="flex gap-3 min-w-0">
                          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary/15 text-primary shrink-0">
                            <Icon className="w-4 h-4" />
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold break-words">{q.question}</p>
                            <p className="text-sm text-muted-foreground break-words">{q.answer}</p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </ProfileModuleShell>
              </div>
            )}

            {/* HER MANTRA — full-width editorial pull-quote */}
            {mantraNode}

            {/* SUPPORT HUB — after About when About tells the story first */}
            {!hubAfterHero && supportHubNode}

            {/* BY THE NUMBERS */}
            {statsNode}



            {/* CAUSES */}
            <ProfileModuleShell
              id="causes"
              active={modules.causes.active}
              title="What Matters to Her"
              action={
                <Link to="/causes" className="text-xs text-primary font-semibold inline-flex items-center gap-1 min-h-[44px] px-1 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                  View All Causes <ArrowRight className="w-3 h-3" />
                </Link>
              }
            >
              <div
                className={
                  readyCauses.length <= 2
                    ? "grid gap-3 sm:grid-cols-2"
                    : "grid grid-cols-2 md:grid-cols-4 gap-3"
                }
              >
                {readyCauses.slice(0, 4).map((c) => {
                  const compact = readyCauses.length <= 2;
                  return (
                    <Link
                      key={c.id}
                      to={`/cause/${c.id}`}
                      className={`rounded-xl hover:bg-muted/60 transition-colors block min-w-0 p-3 ${
                        compact ? "flex items-start gap-3 bg-muted/40" : "text-center"
                      }`}
                    >
                      <div
                        className={`w-12 h-12 rounded-full bg-primary/15 text-primary inline-flex items-center justify-center shrink-0 ${
                          compact ? "" : "mx-auto mb-2"
                        }`}
                      >
                        <Hash className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm break-words">{c.name}</p>
                        {plainText(c.blurb) && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2 break-words">{plainText(c.blurb)}</p>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </ProfileModuleShell>




            {/* PHOTOS */}
            <PhotosSection photos={modules.photos.active ? photos : []} />

            {/* VIDEOS */}
            <VideosSection videos={modules.videos.active ? videos : []} />

            {/* BRAND RELATIONSHIPS */}
            <ProfileModuleShell
              active={modules.brandRelationships.active}
              title={`Brands supporting ${firstName}`}
              action={
                <Link to="/shop" className="text-xs text-primary font-semibold inline-flex items-center gap-1 min-h-[44px] px-1 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                  View All Partners <ArrowRight className="w-3 h-3" />
                </Link>
              }
            >
              {/* Few partners → compact strip of fixed-size tiles; many → an even grid.
                  Never a huge section wrapped around one small logo. */}
              <div
                className={
                  readyProducts.length <= 2
                    ? "flex flex-wrap gap-3 items-center"
                    : "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 items-center"
                }
              >
                {readyProducts.slice(0, 10).map((p) => (
                  <Link
                    key={p.id}
                    to={`/product/${p.id}`}
                    className={`rounded-xl bg-muted/50 hover:bg-muted flex items-center justify-center p-3 transition-colors min-w-0 ${
                      readyProducts.length <= 2 ? "h-20 w-40 shrink-0" : "aspect-[3/2]"
                    }`}
                  >
                    {safeHttpUrl(publicUrl(p.image_path ?? "")) ? (
                      <img
                        src={publicUrl(p.image_path!)!}
                        alt={p.brand_name}
                        className="max-h-full max-w-full object-contain"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                      />
                    ) : (
                      <span className="text-xs font-bold uppercase tracking-wider text-center break-words">{p.brand_name}</span>
                    )}
                  </Link>
                ))}
              </div>

            </ProfileModuleShell>

            {/* DIRECT SUPPORT + PUBLIC SUPPORTER WALL */}
            {modules.directSupport.active && (
              <SupportSection
                athlete={athlete as never}
                supporters={supporters}
                showSupporterWall={modules.publicSupporters.active}
              />
            )}

            {/* PARTNERSHIP INQUIRIES */}
            {modules.partnershipInquiry.active && (
              <WorkWithAthleteSection
                athleteId={athlete.id}
                athleteName={athlete.name}
                partnershipInterests={cleanText(athlete.partnership_interests)}
              />
            )}

            {/* RELATED — inline when there is no rail */}
            {!railOn && relatedNode}

            {isAdmin && <ModuleDiagnostics modules={modules} />}
          </div>

          {/* RIGHT RAIL — only when at least two rail modules are active */}
          {railOn && (
            <aside className="hidden lg:flex lg:flex-col gap-4 lg:sticky lg:top-32">
              {quickFactsNode}
              {connectNode}
              {ctaNode}
              {relatedNode}
            </aside>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

/** Videos get their own section, and render nothing at all when there are none. */
const VideosSection = ({ videos }: { videos: string[] }) => (
  <ProfileModuleShell active={videos.length > 0} title="Videos">
    <div className={`grid gap-3 ${videos.length > 1 ? "md:grid-cols-2" : "max-w-3xl"}`}>
      {videos.map((v, i) => <VideoPlayer key={i} url={v} />)}
    </div>
  </ProfileModuleShell>
);

/**
 * Photos render as their own section. Small collections stay composed: one photo
 * reads as a feature image, two or three fill their row instead of stretching an
 * empty 4-up grid. Nothing renders when there are no photos.
 */
const PhotosSection = ({ photos }: { photos: string[] }) => {
  return (
    <ProfileModuleShell active={photos.length > 0} title="Photos">
      {(
        <div
          className={`grid gap-3 ${
            photos.length === 1
              ? "max-w-md"
              : photos.length === 2
              ? "grid-cols-2 max-w-2xl"
              : photos.length === 3
              ? "grid-cols-2 sm:grid-cols-3"
              : "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
          }`}
        >
          {photos.map((g, i) => (
            <a
              key={i}
              href={g}
              aria-label={`Open photo ${i + 1} of ${photos.length}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`rounded-xl overflow-hidden bg-muted block group ${
                photos.length === 1 ? "aspect-[4/3]" : "aspect-square"
              }`}
            >
              <img
                src={g}
                alt=""
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
                onError={(e) => { (e.currentTarget.parentElement as HTMLElement).style.display = "none"; }}
              />
            </a>
          ))}
        </div>
      )}
    </ProfileModuleShell>
  );
};

export default AthleteProfile;
